import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Paper,
  Typography,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  Button,
  Divider,
  FormControlLabel,
  Checkbox,
  Collapse,
  Box,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import { useTruss } from '../../context/TrussContext';
import { EurocodeText } from '../../utils/eurocodeNotation';

const TUBE_CHORD_TYPES = ['CFSHS', 'CFRHS'];
const OPEN_CHORD_TYPES = ['HEA', 'IPE'];
const DIAG_TYPES = ['CFSHS', 'CFRHS'];

/** Ruukki geometry from manufacturer drawings — modular pitch d, not trough width. */
const RUUKKI_DIAPHRAGM_PROFILES = {
  'T45-30L-905': {
    label: 'Ruukki T45-30L-905',
    effective_width_m: 0.905,
    corrugation_height_mm: 44,
    corrugation_pitch_mm: 90.5,
    trough_mm: 30,
    crest_mm: 60,
  },
  'T70-57L-846': {
    label: 'Ruukki T70-57L-846',
    effective_width_m: 0.846,
    corrugation_height_mm: 70,
    corrugation_pitch_mm: 211.5,
    trough_mm: 57,
    crest_mm: 65,
  },
  'T70-57L-1058': {
    label: 'Ruukki T70-57L-1058',
    effective_width_m: 1.058,
    corrugation_height_mm: 70,
    corrugation_pitch_mm: 211.5,
    trough_mm: 57,
    crest_mm: 65,
  },
  'T130M-75L-930': {
    label: 'Ruukki T130M-75L-930',
    effective_width_m: 0.93,
    corrugation_height_mm: 130,
    corrugation_pitch_mm: 310,
    trough_mm: 75,
    crest_mm: 111,
  },
  'T153-40L-840': {
    label: 'Ruukki T153-40L-840',
    effective_width_m: 0.84,
    corrugation_height_mm: 153,
    corrugation_pitch_mm: 280,
    trough_mm: 40,
    crest_mm: 117,
  },
};
const DEFAULT_DIAPHRAGM_PROFILE = 'T130M-75L-930';

async function loadJson(path) {
  const r = await fetch(path);
  if (!r.ok) return [];
  return r.json();
}

function formatThousandsSpace(raw) {
  const s = String(raw ?? '').trim();
  if (!s) return '';
  if (/e/i.test(s)) return s;
  const n = Number(s.replace(/\s+/g, ''));
  if (!Number.isFinite(n)) return s;
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  const ints = String(Math.trunc(abs)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${sign}${ints}`;
}

const HOLLOW_SECTION_TYPES = new Set(['CFSHS', 'CFRHS', 'HFSHS']);

/** Same gate as ec3_checks.structural_tube_allowed: t ≥ 3 mm and class 1–3. */
function structuralTubeAllowed(row, sectionType) {
  const t = Number(row?.thickness) || 0;
  if (t + 1e-9 < 3) return false;
  const fy = t <= 40 ? 355 : 335;
  const eps = Math.sqrt(235 / fy);
  const h = Number(row?.height) || 0;
  const w = Number(row?.width) || h;
  const hot = sectionType === 'HFSHS';
  let worst = 1;
  for (const b of [Math.max(h, w), Math.min(h, w)]) {
    const c = hot ? b - 2 * t : b - 3 * t;
    if (!(t > 0) || c <= 0) return false;
    const ct = c / t;
    let cl = 4;
    if (ct <= 33 * eps) cl = 1;
    else if (ct <= 38 * eps) cl = 2;
    else if (ct <= 42 * eps) cl = 3;
    if (cl > worst) worst = cl;
  }
  return worst <= 3;
}

function designCatalogRows(sectionType, rows) {
  if (!HOLLOW_SECTION_TYPES.has(sectionType)) return rows || [];
  return (rows || []).filter((r) => structuralTubeAllowed(r, sectionType));
}

function SectionSelect({ label, value, onChange, rows, typeKey }) {
  const list = designCatalogRows(typeKey, rows);
  return (
    <FormControl fullWidth size="small" sx={{ mt: 1 }}>
      <InputLabel>{label}</InputLabel>
      <Select
        label={label}
        value={value?.id != null ? String(value.id) : ''}
        onChange={(e) => {
          const id = Number(e.target.value);
          const row = list.find((x) => Number(x.id) === id);
          onChange(row ? { type: typeKey, id: row.id, name: row.name } : null);
        }}
      >
        {list.map((r) => (
          <MenuItem key={r.id} value={String(r.id)}>
            {r.name} — {r.weight_per_length != null ? `${r.weight_per_length} kg/m` : ''}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export default function TrussInputForm({ onSubmit, loading = false, lastComputeMs = null }) {
  const { inputs, updateInputs } = useTruss();
  const [catalog, setCatalog] = useState({});

  const chordTypes = inputs.chord_family === 'open' ? OPEN_CHORD_TYPES : TUBE_CHORD_TYPES;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      /* Always load HEA/IPE for portal columns; chord + diagonal catalogs for truss members */
      const keys = [...new Set([...chordTypes, ...DIAG_TYPES, ...OPEN_CHORD_TYPES])];
      const next = { ...catalog };
      for (const k of keys) {
        const file =
          k === 'HEA'
            ? '/HEA_sections.json'
            : k === 'IPE'
              ? '/IPE_sections.json'
              : k === 'CFSHS'
                ? '/CFSHS_sections.json'
                : k === 'CFRHS'
                  ? '/CFRHS_sections.json'
                  : '/CFSHS_sections.json';
        next[k] = designCatalogRows(k, await loadJson(file));
      }
      if (!cancelled) setCatalog(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [inputs.chord_family]);

  const topType = inputs.top_chord?.type || chordTypes[0];
  const topTypeLeft = inputs.top_chord_left?.type || topType;
  const topTypeRight = inputs.top_chord_right?.type || topType;
  const botType = inputs.bottom_chord?.type || chordTypes[0];
  const diagType = inputs.diagonal?.type || 'CFSHS';
  const diag2Type = inputs.diagonal_2?.type || diagType;
  const diag3Type = inputs.diagonal_3?.type || diagType;
  const nDiagGroups = Math.min(10, Math.max(1, Number(inputs.diagonal_group_count) || 3));
  const isBuilding = !!inputs.building_mode;
  const designBasis =
    inputs.design_code === 'aisc_lrfd' || inputs.design_profile === 'asce_aisc'
      ? 'aisc'
      : String(inputs.national_annex || 'FI').toUpperCase().startsWith('IS')
        ? 'eurocode_is'
        : String(inputs.national_annex || '').toUpperCase().startsWith('LV')
          ? 'eurocode_lv'
          : 'eurocode_fi';
  const setDesignBasis = (value) => {
    if (value === 'aisc') {
      updateInputs({ design_code: 'aisc_lrfd', design_profile: 'asce_aisc', national_annex: 'AISC' });
      return;
    }
    if (value === 'eurocode_is') {
      updateInputs({
        design_code: 'eurocode',
        design_profile: 'sfs_en',
        national_annex: 'IS',
        wind_speed_ms: inputs.wind_speed_explicit ? inputs.wind_speed_ms : inputs.wind_coastal_exposure ? 46 : 36,
      });
      return;
    }
    if (value === 'eurocode_lv') {
      updateInputs({
        design_code: 'eurocode',
        design_profile: 'sfs_en',
        national_annex: 'LV',
        wind_speed_ms: '',
        wind_speed_explicit: false,
        sk_kN_m2: '',
        snow_sk_source: 'manual',
      });
      return;
    }
    updateInputs({
      design_code: 'eurocode',
      design_profile: 'sfs_en',
      national_annex: 'FI',
      wind_speed_ms: inputs.wind_speed_explicit ? inputs.wind_speed_ms : 21,
    });
  };
  const showFiSnowMap = (isBuilding || !!inputs.snow_model_en1991) && designBasis === 'eurocode_fi';
  const [snowLookupBusy, setSnowLookupBusy] = useState(false);
  const [snowLookupError, setSnowLookupError] = useState('');

  const lookupSnowSk = async () => {
    setSnowLookupError('');
    setSnowLookupBusy(true);
    try {
      const coordSystem = inputs.site_coord_system || 'wgs84';
      const body =
        coordSystem === 'tm35fin'
          ? {
              coord_system: 'tm35fin',
              northing: Number(inputs.site_northing),
              easting: Number(inputs.site_easting),
            }
          : {
              coord_system: 'wgs84',
              lat: Number(inputs.site_lat),
              lon: Number(inputs.site_lon),
            };
      const res = await axios.post('/api/truss-analysis/calculations/lookup-snow-sk/', body);
      const sk = res.data?.sk_kN_m2;
      if (sk == null || Number.isNaN(Number(sk))) {
        setSnowLookupError('Lookup returned no value.');
        return;
      }
      updateInputs({ sk_kN_m2: sk, snow_sk_source: 'coordinates' });
    } catch (err) {
      setSnowLookupError(err.response?.data?.error || err.message || 'Snow lookup failed.');
    } finally {
      setSnowLookupBusy(false);
    }
  };
  const frameSpacing = Math.max(Number(inputs.frame_spacing_m) || 6, 0.1);
  const archedHall = inputs.shape === 'arch';
  const trussedArch = archedHall && (inputs.arch_system || 'beam') === 'truss';

  const setAnalysisMode = (building) => {
    if (building === isBuilding) return;
    const patch = { building_mode: building };
    if (building) {
      patch.g_kN_m2 = Number(inputs.g_kN_m2 ?? 0) || Number(inputs.g_kN_m || 0) / frameSpacing;
      patch.imposed_kN_m2 = Number(inputs.imposed_kN_m2 ?? 0) || Number(inputs.imposed_kN_m || 0) / frameSpacing;
      patch.wind_model_en1991 = inputs.design_code !== 'aisc_lrfd';
      patch.wind_model_manual = false;
      if (inputs.design_code !== 'aisc_lrfd') patch.snow_model_en1991 = true;
      if (inputs.concentrically_braced_mode || inputs.stability_system === 'concentrically_braced') {
        patch.structure_type = 'truss_pinned';
        patch.stability_system = 'concentrically_braced';
        patch.concentrically_braced_mode = true;
      } else if (inputs.stability_system === 'portal') {
        patch.structure_type = 'portal';
      } else {
        // Whole-building default: OCBF (pinned truss + concentric bracing)
        patch.structure_type = 'truss_pinned';
        patch.stability_system = 'concentrically_braced';
        patch.concentrically_braced_mode = true;
      }
    } else {
      patch.g_kN_m = Number(inputs.g_kN_m || 0) || Number(inputs.g_kN_m2 ?? 0) * frameSpacing;
      patch.imposed_kN_m = Number(inputs.imposed_kN_m || 0) || Number(inputs.imposed_kN_m2 ?? 0) * frameSpacing;
      patch.wind_model_en1991 = false;
      patch.wind_model_manual = false;
      patch.snow_model_en1991 = false;
    }
    updateInputs(patch);
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Truss configuration
      </Typography>

      <ToggleButtonGroup
        exclusive
        fullWidth
        size="small"
        value={isBuilding ? 'building' : 'single'}
        onChange={(_, v) => {
          if (v) setAnalysisMode(v === 'building');
        }}
        sx={{ mb: 2 }}
      >
        <ToggleButton value="single">Single truss / frame</ToggleButton>
        <ToggleButton value="building">Whole building</ToggleButton>
      </ToggleButtonGroup>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Chord family</InputLabel>
            <Select
              label="Chord family"
              value={inputs.chord_family}
              onChange={(e) => updateInputs({ chord_family: e.target.value })}
            >
              <MenuItem value="tube">Tubes (SHS/RHS)</MenuItem>
              <MenuItem value="open">Open (HEA / IPE)</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Structure</InputLabel>
            <Select
              label="Structure"
              value={inputs.structure_type}
              onChange={(e) => updateInputs({ structure_type: e.target.value })}
            >
              <MenuItem value="truss_pinned">Truss (pinned + roller)</MenuItem>
              <MenuItem
                value="truss_cantilever"
                title="Same pin + roller supports as pinned truss; auto-design uses cantilever beam load analogy (M ≈ wL²/2) instead of simply-supported."
              >
                Truss (cantilever design analogy)
              </MenuItem>
              <MenuItem value="portal">Portal (columns to base)</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Shape</InputLabel>
            <Select
              label="Shape"
              value={inputs.shape}
              onChange={(e) => {
                const shape = e.target.value;
                const patch = { shape };
                if (shape === 'dual') {
                  const hL = Number(inputs.height_left_m) || 1.2;
                  const hR = Number(inputs.height_right_m) || 0;
                  if (!hR) patch.height_right_m = hL;
                  const ridge = inputs.dual_ridge_height_m;
                  if (ridge === '' || ridge == null) {
                    patch.dual_ridge_height_m = Math.max(hL, hR || hL) + 0.8;
                  }
                }
                updateInputs(patch);
              }}
            >
              <MenuItem value="flat">Flat</MenuItem>
              <MenuItem value="mono">Mono-slope</MenuItem>
              <MenuItem value="dual">Dual-pitch (ridge)</MenuItem>
              <MenuItem value="arch">Arched hall</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Pattern</InputLabel>
            <Select label="Pattern" value={inputs.pattern} onChange={(e) => updateInputs({ pattern: e.target.value })}>
              <MenuItem value="warren">Warren</MenuItem>
              <MenuItem value="warren_vert">Warren + verticals</MenuItem>
              <MenuItem value="pratt">Pratt</MenuItem>
              <MenuItem value="howe">Howe</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        {inputs.shape === 'arch' && (
          <>
            <Grid item xs={6} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Arch rise f (m)"
                type="number"
                value={inputs.arch_rise_m ?? ''}
                onChange={(e) => updateInputs({ arch_rise_m: e.target.value, height_left_m: e.target.value })}
                helperText="Crown above the springing"
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Column height (m)"
                type="number"
                value={inputs.arch_springing_height_m ?? 0}
                onChange={(e) => updateInputs({ arch_springing_height_m: e.target.value })}
                helperText="0 = arch to foundation"
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Arch system</InputLabel>
                <Select
                  label="Arch system"
                  value={inputs.arch_system || 'beam'}
                  onChange={(e) => updateInputs({ arch_system: e.target.value })}
                >
                  <MenuItem value="beam">Beam rib</MenuItem>
                  <MenuItem value="truss">Trussed arch</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Arc segments"
                type="number"
                value={inputs.arch_segments ?? 16}
                onChange={(e) => updateInputs({ arch_segments: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Base fixity</InputLabel>
                <Select
                  label="Base fixity"
                  value={inputs.arch_base_fixity || 'pinned'}
                  onChange={(e) => updateInputs({ arch_base_fixity: e.target.value })}
                >
                  <MenuItem value="pinned">Pinned springings</MenuItem>
                  <MenuItem value="fixed">Fixed springings</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {(inputs.arch_system || 'beam') === 'truss' && (
              <>
                <Grid item xs={6} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Arch curve</InputLabel>
                    <Select
                      label="Arch curve"
                      value={inputs.arch_curve || 'circular'}
                      onChange={(e) => updateInputs({ arch_curve: e.target.value })}
                    >
                      <MenuItem value="circular">Circular</MenuItem>
                      <MenuItem value="parabolic">Parabolic</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Depth normal to arch (m)"
                    type="number"
                    value={inputs.arch_depth_m ?? 0.8}
                    onChange={(e) => updateInputs({ arch_depth_m: e.target.value })}
                    helperText="Constant along the inward normal"
                  />
                </Grid>
                {Number(inputs.arch_springing_height_m) > 0 && (
                  <Grid item xs={6} sm={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Columns</InputLabel>
                      <Select
                        label="Columns"
                        value={inputs.arch_column_taper || 'tapered'}
                        onChange={(e) => updateInputs({ arch_column_taper: e.target.value })}
                      >
                        <MenuItem value="tapered">Tapered</MenuItem>
                        <MenuItem value="straight">Straight</MenuItem>
                      </Select>
                      <FormHelperText>
                        {(inputs.arch_column_taper || 'tapered') === 'straight'
                          ? 'Both chords vertical'
                          : 'Narrows toward the base, not closer than both chord depths + 100 mm'}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                )}
              </>
            )}
          </>
        )}
        <Grid item xs={6} sm={4}>
          <TextField
            fullWidth
            size="small"
            label="Span (m)"
            type="number"
            value={inputs.span_m}
            onChange={(e) => updateInputs({ span_m: e.target.value })}
          />
        </Grid>
        {!trussedArch && (
          <Grid item xs={6} sm={4}>
            <TextField
              fullWidth
              size="small"
              label={inputs.shape === 'flat' ? 'Truss height (m)' : 'Height left (m)'}
              type="number"
              value={inputs.height_left_m}
              onChange={(e) => updateInputs({ height_left_m: e.target.value })}
            />
          </Grid>
        )}
        {inputs.shape === 'dual' && (
          <Grid item xs={6} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Height ridge (m)"
              type="number"
              value={inputs.dual_ridge_height_m}
              onChange={(e) => updateInputs({ dual_ridge_height_m: e.target.value })}
            />
          </Grid>
        )}
        {inputs.shape !== 'flat' && !trussedArch && (
          <Grid item xs={6} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Height right (m)"
              type="number"
              value={inputs.height_right_m}
              onChange={(e) => updateInputs({ height_right_m: e.target.value })}
            />
          </Grid>
        )}
        {inputs.shape === 'dual' && (
          <Grid item xs={6} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Ridge offset (m)"
              type="number"
              value={inputs.ridge_offset_m}
              onChange={(e) => updateInputs({ ridge_offset_m: e.target.value })}
              helperText="From mid-span (+ toward right)"
            />
          </Grid>
        )}
        {!trussedArch && (
          <Grid item xs={6} sm={4}>
            <Tooltip
              title="Insets both chord ends from the outer span. Net panel span = span − 2×ecc. Used with bearing offset for web bay layout."
              arrow
            >
              <TextField
                fullWidth
                size="small"
                label="Eccentricity (mm)"
                type="number"
                value={inputs.eccentricity_mm}
                onChange={(e) => updateInputs({ eccentricity_mm: e.target.value })}
              />
            </Tooltip>
          </Grid>
        )}
        {!trussedArch && (
          <Grid item xs={6} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Roller end</InputLabel>
              <Select label="Roller end" value={inputs.roller_on} onChange={(e) => updateInputs({ roller_on: e.target.value })}>
                <MenuItem value="right">Right</MenuItem>
                <MenuItem value="left">Left</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        )}
        {!archedHall && (
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small" disabled={inputs.shape !== 'flat'}>
              <InputLabel>Bearing / supports on</InputLabel>
              <Select
                label="Bearing / supports on"
                value={inputs.support_placement || 'bottom'}
                onChange={(e) => updateInputs({ support_placement: e.target.value })}
              >
                <MenuItem value="bottom">Bottom chord</MenuItem>
                <MenuItem value="top">Top chord (flat truss)</MenuItem>
              </Select>
            </FormControl>
            {inputs.shape !== 'flat' && (
              <Typography variant="caption" color="text.secondary">
                Mono / dual layouts use bottom supports in this version.
              </Typography>
            )}
          </Grid>
        )}
        {inputs.shape === 'flat' && (
          <Grid item xs={12} sm={6}>
            <Tooltip
              title={
                inputs.support_placement === 'top'
                  ? 'Insets the first/last web panel from the bearing: equal bays over (span − 2×ecc − 2×offset); end diagonals get steeper. Also used for end top-chord N+M check (reaction × offset).'
                  : 'Same chord layout inset for flat trusses: web does not terminate at the support node; equal bays in the clear span. Set 0 for a uniform grid to supports.'
              }
              arrow
            >
              <TextField
                fullWidth
                size="small"
                label="Bearing connection offset (m)"
                type="number"
                value={inputs.bearing_connection_offset_m}
                onChange={(e) => updateInputs({ bearing_connection_offset_m: e.target.value })}
              />
            </Tooltip>
          </Grid>
        )}
      </Grid>

      <Divider sx={{ my: 2 }} />
      <Collapse in={isBuilding}>
        <Typography variant="subtitle2" gutterBottom>
          Building layout &amp; bracing
        </Typography>
        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Transverse stability system</InputLabel>
              <Select
                label="Transverse stability system"
                value={inputs.concentrically_braced_mode ? 'concentrically_braced' : 'portal'}
                onChange={(e) => {
                  const cbf = e.target.value === 'concentrically_braced';
                  updateInputs({
                    concentrically_braced_mode: cbf,
                    stability_system: cbf ? 'concentrically_braced' : 'portal',
                    structure_type: cbf ? 'truss_pinned' : inputs.structure_type === 'truss_pinned' ? 'portal' : inputs.structure_type,
                    second_order_p_delta: cbf ? false : inputs.second_order_p_delta,
                  });
                }}
              >
                <MenuItem value="portal">Portal frame (moment resistance)</MenuItem>
                <MenuItem value="concentrically_braced">Concentrically braced (pinned tube diagonals)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="Building length (m)"
              type="number"
              value={inputs.building_length_m}
              onChange={(e) => updateInputs({ building_length_m: e.target.value })}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="Frame spacing (m)"
              type="number"
              value={inputs.frame_spacing_m}
              onChange={(e) => updateInputs({ frame_spacing_m: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              Column height is clear height from bottom chord / eave to base. Truss depth remains in geometry above.
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="Column left H (m)"
              type="number"
              value={inputs.column_left_m}
              onChange={(e) => updateInputs({ column_left_m: e.target.value })}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="Column right H (m)"
              type="number"
              value={inputs.column_right_m}
              onChange={(e) => updateInputs({ column_right_m: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Frame spacings (m, comma-separated)"
              placeholder="6, 6, 6"
              helperText="Optional override; leave empty for uniform spacing"
              value={inputs.frame_spacings_m}
              onChange={(e) => updateInputs({ frame_spacings_m: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Bracing layout</InputLabel>
              <Select
                label="Bracing layout"
                value={inputs.bracing_layout_mode || 'auto'}
                onChange={(e) => updateInputs({ bracing_layout_mode: e.target.value })}
              >
                <MenuItem value="auto">Auto (OCBF mid HT/VB + end gable VB)</MenuItem>
                <MenuItem value="manual">Manual override</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Wind column max spacing (m)"
              helperText="Eaves (along length) and gable ends (along span)"
              type="number"
              inputProps={{ min: 0.5, max: 20, step: 0.5 }}
              value={inputs.wind_column_max_spacing_m}
              onChange={(e) => updateInputs({ wind_column_max_spacing_m: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Transverse brace frame lines (gable VB + HT)"
              placeholder="0, 4 — empty = auto ends for OCBF"
              value={inputs.transverse_brace_frame_indices ?? inputs.vertical_bracing_bays ?? ''}
              onChange={(e) =>
                updateInputs({
                  transverse_brace_frame_indices: e.target.value,
                  vertical_bracing_bays: e.target.value,
                })
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Lengthwise brace frame lines (sidewall VB + roof HT)"
              placeholder="2 — empty = auto mid interior frame for OCBF"
              value={inputs.lengthwise_brace_frame_indices}
              onChange={(e) => updateInputs({ lengthwise_brace_frame_indices: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Tooltip
              title="Semi-rigid credits profiled sheeting as a roof diaphragm (EN 1993-1-3 §10.3). Selecting it enables stressed-skin bracing and top-chord lateral restraint automatically."
              arrow
            >
              <FormControl fullWidth size="small">
                <InputLabel>Roof diaphragm</InputLabel>
                <Select
                  label="Roof diaphragm"
                  value={inputs.roof_diaphragm || 'none'}
                  onChange={(e) => updateInputs({ roof_diaphragm: e.target.value })}
                >
                  <MenuItem value="none">None (explicit roof bracing only)</MenuItem>
                  <MenuItem value="semi_rigid">Semi-rigid (stressed skin)</MenuItem>
                  <MenuItem value="rigid">Rigid (sensitivity only — blocked)</MenuItem>
                </Select>
              </FormControl>
            </Tooltip>
          </Grid>
          {inputs.roof_diaphragm === 'semi_rigid' && (
            <>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!inputs.diaphragm_structural_ack}
                      onChange={(e) => updateInputs({ diaphragm_structural_ack: e.target.checked })}
                    />
                  }
                  label="I acknowledge structural credit for this stressed-skin diaphragm"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!inputs.diaphragm_na_permitted}
                      onChange={(e) => updateInputs({ diaphragm_na_permitted: e.target.checked })}
                    />
                  }
                  label="The national annex in force permits stressed-skin design"
                />
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info" sx={{ py: 0.5 }}>
                  Ruukki profiles: S from ECCS No. 88 using sheet geometry, frame spacing as panel length a, span as
                  depth b, S350GD+Z, and <strong>fasteners in every trough</strong> (declared in results). Enter
                  in-plane V<sub>Rd</sub> and edge-member area. Custom = enter S yourself.
                </Alert>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Tooltip
                  title="Cover width + modular pitch d from Ruukki drawings. Pitch d is the repeating rib (e.g. 310 mm for T130M), not the trough flange."
                  arrow
                >
                  <FormControl fullWidth size="small">
                    <InputLabel>Sheet profile</InputLabel>
                    <Select
                      label="Sheet profile"
                      value={inputs.diaphragm_profile_id || DEFAULT_DIAPHRAGM_PROFILE}
                      onChange={(e) => {
                        const id = e.target.value;
                        const custom = id === 'custom';
                        updateInputs({
                          diaphragm_profile_id: id,
                          diaphragm_property_source: custom ? 'manual_override' : 'eccs88_calculated',
                          ...(custom
                            ? {}
                            : {
                                diaphragm_c_panel_mm_per_kn: '',
                                diaphragm_s_kn: '',
                                diaphragm_stiffness_kN_per_m: '',
                              }),
                        });
                      }}
                    >
                      {Object.entries(RUUKKI_DIAPHRAGM_PROFILES).map(([id, p]) => (
                        <MenuItem key={id} value={id}>
                          {p.label}
                        </MenuItem>
                      ))}
                      <MenuItem value="custom">Custom (enter S)</MenuItem>
                    </Select>
                  </FormControl>
                </Tooltip>
                {(inputs.diaphragm_profile_id || DEFAULT_DIAPHRAGM_PROFILE) !== 'custom' &&
                  RUUKKI_DIAPHRAGM_PROFILES[inputs.diaphragm_profile_id || DEFAULT_DIAPHRAGM_PROFILE] && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      {(() => {
                        const p =
                          RUUKKI_DIAPHRAGM_PROFILES[inputs.diaphragm_profile_id || DEFAULT_DIAPHRAGM_PROFILE];
                        return (
                          <>
                            cover = {(p.effective_width_m * 1000).toFixed(0)} mm · trough = {p.trough_mm} mm ·
                            crest = {p.crest_mm} mm · pitch d = {p.corrugation_pitch_mm} mm · h ={' '}
                            {p.corrugation_height_mm} mm · S350GD+Z · every trough
                          </>
                        );
                      })()}
                    </Typography>
                  )}
              </Grid>
              {(inputs.diaphragm_profile_id || DEFAULT_DIAPHRAGM_PROFILE) === 'custom' ? (
                <Grid item xs={12} sm={6}>
                  <Tooltip title="Panel in-plane shear stiffness S (kN). Custom mode only." arrow>
                    <TextField
                      fullWidth
                      size="small"
                      label="S (kN)"
                      required
                      value={inputs.diaphragm_s_kn || inputs.diaphragm_stiffness_kN_per_m}
                      onChange={(e) =>
                        updateInputs({
                          diaphragm_s_kn: e.target.value,
                          diaphragm_stiffness_kN_per_m: e.target.value,
                          diaphragm_c_panel_mm_per_kn: '',
                        })
                      }
                    />
                  </Tooltip>
                </Grid>
              ) : (
                <Grid item xs={12} sm={3}>
                  <Tooltip title="Core sheet thickness for ECCS flexibility (excl. coatings). Default 0.75 mm." arrow>
                    <TextField
                      fullWidth
                      size="small"
                      label="Thickness t (mm)"
                      value={inputs.diaphragm_thickness_mm ?? 0.75}
                      onChange={(e) => updateInputs({ diaphragm_thickness_mm: e.target.value })}
                    />
                  </Tooltip>
                </Grid>
              )}
              <Grid item xs={12} sm={4}>
                <Tooltip
                  title="Design in-plane (lateral) diaphragm shear resistance — not vertical Vw,Rd. Still required (seam/fastener modes often govern)."
                  arrow
                >
                  <TextField
                    fullWidth
                    size="small"
                    label="V_Rd,in-plane (kN)"
                    value={inputs.diaphragm_v_rd_kn}
                    onChange={(e) => updateInputs({ diaphragm_v_rd_kn: e.target.value })}
                  />
                </Tooltip>
              </Grid>
              {(inputs.diaphragm_profile_id || DEFAULT_DIAPHRAGM_PROFILE) === 'custom' && (
                <Grid item xs={12} sm={4}>
                  <Tooltip title="Diaphragm panel length along the building. Ruukki profiles use frame spacing." arrow>
                    <TextField
                      fullWidth
                      size="small"
                      label="Panel length L_s (m)"
                      value={inputs.diaphragm_panel_length_m}
                      onChange={(e) => updateInputs({ diaphragm_panel_length_m: e.target.value })}
                    />
                  </Tooltip>
                </Grid>
              )}
              <Grid item xs={12} sm={4}>
                <Tooltip
                  title="Cross-section area of longitudinal edge members that collect diaphragm shear into the bracing/frames. Required for a complete load path."
                  arrow
                >
                  <TextField
                    fullWidth
                    size="small"
                    label="Edge member area (mm²)"
                    value={inputs.diaphragm_edge_member_area_mm2}
                    onChange={(e) => updateInputs({ diaphragm_edge_member_area_mm2: e.target.value })}
                  />
                </Tooltip>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Tooltip title="SLS limit on in-plane diaphragm drift under longitudinal wind (absolute mm)." arrow>
                  <TextField
                    fullWidth
                    size="small"
                    label="SLS drift limit (mm)"
                    value={inputs.diaphragm_sls_drift_limit_mm}
                    onChange={(e) => updateInputs({ diaphragm_sls_drift_limit_mm: e.target.value })}
                  />
                </Tooltip>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Tooltip
                  title="Spacing used as top-chord L_lat when sheeting provides continuous lateral restraint (typically fastener/purlin spacing). Leave blank to use support span."
                  arrow
                >
                  <TextField
                    fullWidth
                    size="small"
                    label="Restraint spacing (m)"
                    value={inputs.diaphragm_member_restraint_spacing_m}
                    onChange={(e) => updateInputs({ diaphragm_member_restraint_spacing_m: e.target.value })}
                    placeholder="defaults to support span"
                  />
                </Tooltip>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!inputs.sn027_bottom_chord_check}
                      onChange={(e) => updateInputs({ sn027_bottom_chord_check: e.target.checked })}
                    />
                  }
                  label="SN027a bottom chord check (CBF + compression + sheeting restraint)"
                />
              </Grid>
              {inputs.sn027_bottom_chord_check && (
                <>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Purlin I (m⁴)"
                      placeholder="7.33e-6"
                      value={inputs.sn027_purlin_I_m4}
                      onChange={(e) => updateInputs({ sn027_purlin_I_m4: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Web member I (m⁴)"
                      placeholder="1.07e-6"
                      value={inputs.sn027_web_I_m4}
                      onChange={(e) => updateInputs({ sn027_web_I_m4: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Screw eccentricity e (m)"
                      value={inputs.sn027_screw_eccentricity_m}
                      onChange={(e) => updateInputs({ sn027_screw_eccentricity_m: e.target.value })}
                    />
                  </Grid>
                </>
              )}
            </>
          )}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Analysis stage</InputLabel>
              <Select
                label="Analysis stage"
                value={inputs.analysis_stage || 'completed'}
                onChange={(e) => updateInputs({ analysis_stage: e.target.value })}
              >
                <MenuItem value="completed">Completed building</MenuItem>
                <MenuItem value="erection">Erection (partial base fixity)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Design basis</InputLabel>
              <Select
                label="Design basis"
                value={designBasis}
                onChange={(e) => setDesignBasis(e.target.value)}
              >
                <MenuItem value="eurocode_fi">Second-generation Eurocode, annex FI (recommended)</MenuItem>
                <MenuItem value="eurocode_is">Second-generation Eurocode, annex IS (recommended)</MenuItem>
                <MenuItem value="eurocode_lv">Eurocode, annex LV (enter s_k and v_b,0)</MenuItem>
                <MenuItem value="aisc">AISC 360 LRFD</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {inputs.design_code !== 'aisc_lrfd' && (
            <>
              <Grid item xs={6} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label={<EurocodeText>Wind speed v_b,0 (m/s)</EurocodeText>}
                  helperText={
                    designBasis === 'eurocode_is'
                      ? 'Iceland default 36 m/s, or 46 m/s on an open coast'
                      : designBasis === 'eurocode_lv'
                        ? 'Latvia: enter v_b,0. No LVS wind map is stored.'
                        : 'Finnish default 21 m/s'
                  }
                  type="number"
                  value={inputs.wind_speed_ms}
                  onChange={(e) => updateInputs({ wind_speed_ms: e.target.value, wind_speed_explicit: true })}
                />
              </Grid>
              {designBasis === 'eurocode_is' && (
                <Grid item xs={12} sm={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!inputs.wind_coastal_exposure}
                        onChange={(e) =>
                          updateInputs({
                            wind_coastal_exposure: e.target.checked,
                            wind_speed_ms: inputs.wind_speed_explicit ? inputs.wind_speed_ms : e.target.checked ? 46 : 36,
                          })
                        }
                      />
                    }
                    label="Open coast (v_b,0 = 46 m/s)"
                  />
                </Grid>
              )}
              <Grid item xs={6} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Terrain category</InputLabel>
                  <Select
                    label="Terrain category"
                    value={inputs.terrain_category || 'II'}
                    onChange={(e) => updateInputs({ terrain_category: e.target.value })}
                  >
                    <MenuItem value="0">0 — coastal / open sea</MenuItem>
                    <MenuItem value="I">I</MenuItem>
                    <MenuItem value="II">II</MenuItem>
                    <MenuItem value="III">III</MenuItem>
                    <MenuItem value="IV">IV</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}
          {inputs.concentrically_braced_mode && (
            <Grid item xs={12}>
              <Alert severity="info" sx={{ py: 0.5 }}>
                CBF mode: bottom chord is axially released at columns; top chord is moment-pinned. Use vertical + roof
                bracing at expansion-zone ends.
              </Alert>
            </Grid>
          )}
        </Grid>
        <Divider sx={{ my: 2 }} />
      </Collapse>

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" gutterBottom>
        Chord profile &amp; bracing
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!inputs.manual_sections}
                onChange={(e) => updateInputs({ manual_sections: e.target.checked })}
              />
            }
            label="Manual section selection (advanced)"
          />
        </Grid>
        {inputs.chord_family === 'open' && !inputs.manual_sections && (
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Open chord profile</InputLabel>
              <Select
                label="Open chord profile"
                value={inputs.open_chord_shape || 'HEA'}
                onChange={(e) => updateInputs({ open_chord_shape: e.target.value })}
              >
                {OPEN_CHORD_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label="Lateral buckling length"
            placeholder="1p"
            helperText="Metres or factor×panel. Prefilled 1p (one panel between webs). Clear for the full unrestrained chord, or OCBF spacing on a building."
            value={inputs.chord_bracing_lateral_m}
            onChange={(e) => updateInputs({ chord_bracing_lateral_m: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label="In-plane chord buckling length"
            placeholder="1p"
            helperText="Metres or factor×panel. Prefilled 1p (one panel between webs). Clear for the full chord length."
            value={inputs.chord_bracing_truss_plane_m}
            onChange={(e) => updateInputs({ chord_bracing_truss_plane_m: e.target.value })}
          />
        </Grid>
        {inputs.chord_family === 'tube' && !inputs.manual_sections && (
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Tube width match ± (mm)"
              type="number"
              value={inputs.tube_width_match_mm}
              onChange={(e) => updateInputs({ tube_width_match_mm: e.target.value })}
            />
          </Grid>
        )}
      </Grid>

      {!!inputs.manual_sections && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>
            Sections
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Top chord type</InputLabel>
                <Select
                  label="Top chord type"
                  value={topType}
                  onChange={(e) => updateInputs({ top_chord: { type: e.target.value, id: catalog[e.target.value]?.[0]?.id } })}
                >
                  {chordTypes.map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <SectionSelect
                label="Top chord"
                value={inputs.top_chord}
                typeKey={topType}
                rows={catalog[topType]}
                onChange={(v) => updateInputs({ top_chord: v })}
              />
            </Grid>
            {inputs.shape === 'dual' && (
              <>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Top chord left type</InputLabel>
                    <Select
                      label="Top chord left type"
                      value={topTypeLeft}
                      onChange={(e) =>
                        updateInputs({
                          top_chord_left: { type: e.target.value, id: catalog[e.target.value]?.[0]?.id },
                        })
                      }
                    >
                      {chordTypes.map((t) => (
                        <MenuItem key={t} value={t}>
                          {t}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <SectionSelect
                    label="Top chord (left)"
                    value={inputs.top_chord_left || inputs.top_chord}
                    typeKey={topTypeLeft}
                    rows={catalog[topTypeLeft]}
                    onChange={(v) => updateInputs({ top_chord_left: v })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Top chord right type</InputLabel>
                    <Select
                      label="Top chord right type"
                      value={topTypeRight}
                      onChange={(e) =>
                        updateInputs({
                          top_chord_right: { type: e.target.value, id: catalog[e.target.value]?.[0]?.id },
                        })
                      }
                    >
                      {chordTypes.map((t) => (
                        <MenuItem key={t} value={t}>
                          {t}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <SectionSelect
                    label="Top chord (right)"
                    value={inputs.top_chord_right || inputs.top_chord}
                    typeKey={topTypeRight}
                    rows={catalog[topTypeRight]}
                    onChange={(v) => updateInputs({ top_chord_right: v })}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Bottom chord type</InputLabel>
                <Select
                  label="Bottom chord type"
                  value={botType}
                  onChange={(e) => updateInputs({ bottom_chord: { type: e.target.value, id: catalog[e.target.value]?.[0]?.id } })}
                >
                  {chordTypes.map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <SectionSelect
                label="Bottom chord"
                value={inputs.bottom_chord}
                typeKey={botType}
                rows={catalog[botType]}
                onChange={(v) => updateInputs({ bottom_chord: v })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Diagonal type</InputLabel>
                <Select
                  label="Diagonal type"
                  value={diagType}
                  onChange={(e) => updateInputs({ diagonal: { type: e.target.value, id: catalog[e.target.value]?.[0]?.id } })}
                >
                  {DIAG_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <SectionSelect
                label="Diagonal (group 1)"
                value={inputs.diagonal}
                typeKey={diagType}
                rows={catalog[diagType]}
                onChange={(v) => updateInputs({ diagonal: v })}
              />
            </Grid>
            {nDiagGroups >= 2 && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Diagonal type (group 2)</InputLabel>
                  <Select
                    label="Diagonal type (group 2)"
                    value={diag2Type}
                    onChange={(e) => updateInputs({ diagonal_2: { type: e.target.value, id: catalog[e.target.value]?.[0]?.id } })}
                  >
                    {DIAG_TYPES.map((t) => (
                      <MenuItem key={t} value={t}>
                        {t}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <SectionSelect
                  label="Diagonal (group 2)"
                  value={inputs.diagonal_2 || inputs.diagonal}
                  typeKey={diag2Type}
                  rows={catalog[diag2Type]}
                  onChange={(v) => updateInputs({ diagonal_2: v })}
                />
              </Grid>
            )}
            {nDiagGroups >= 3 && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Diagonal type (group 3)</InputLabel>
                  <Select
                    label="Diagonal type (group 3)"
                    value={diag3Type}
                    onChange={(e) => updateInputs({ diagonal_3: { type: e.target.value, id: catalog[e.target.value]?.[0]?.id } })}
                  >
                    {DIAG_TYPES.map((t) => (
                      <MenuItem key={t} value={t}>
                        {t}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <SectionSelect
                  label="Diagonal (group 3)"
                  value={inputs.diagonal_3 || inputs.diagonal}
                  typeKey={diag3Type}
                  rows={catalog[diag3Type]}
                  onChange={(v) => updateInputs({ diagonal_3: v })}
                />
              </Grid>
            )}
            {['pratt', 'howe', 'warren_vert'].includes(inputs.pattern) && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Vertical type</InputLabel>
                  <Select
                    label="Vertical type"
                    value={inputs.vertical?.type || diagType}
                    onChange={(e) =>
                      updateInputs({ vertical: { type: e.target.value, id: catalog[e.target.value]?.[0]?.id } })
                    }
                  >
                    {DIAG_TYPES.map((t) => (
                      <MenuItem key={t} value={t}>
                        {t}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <SectionSelect
                  label="Vertical member"
                  value={inputs.vertical || inputs.diagonal}
                  typeKey={inputs.vertical?.type || diagType}
                  rows={catalog[inputs.vertical?.type || diagType]}
                  onChange={(v) => updateInputs({ vertical: v })}
                />
              </Grid>
            )}
          </Grid>
        </>
      )}

      <Collapse in={inputs.structure_type === 'portal' && !inputs.concentrically_braced_mode}>
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" gutterBottom>
          Portal columns
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Column size is auto-selected (lightest HEA/IPE that passes) unless you lock the section below.
        </Typography>
        <Grid container spacing={2}>
          {!isBuilding && (
            <>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Column left H (m)"
                  type="number"
                  value={inputs.column_left_m}
                  onChange={(e) => updateInputs({ column_left_m: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Column right H (m)"
                  type="number"
                  value={inputs.column_right_m}
                  onChange={(e) => updateInputs({ column_right_m: e.target.value })}
                />
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!inputs.lock_column_section}
                  onChange={(e) => updateInputs({ lock_column_section: e.target.checked })}
                />
              }
              label="Lock column section (disable auto-sizing)"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Column section type</InputLabel>
              <Select
                label="Column section type"
                value={inputs.column?.type || inputs.column_section_type || 'HEA'}
                onChange={(e) => {
                  const t = e.target.value;
                  updateInputs({
                    column_section_type: t,
                    column: { type: t, id: catalog[t]?.[0]?.id },
                  });
                }}
              >
                {OPEN_CHORD_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <SectionSelect
              label="Column"
              value={inputs.column}
              typeKey={inputs.column?.type || 'HEA'}
              rows={catalog[inputs.column?.type || 'HEA']}
              onChange={(v) => updateInputs({ column: v })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Column base rotational stiffness (kN·m/rad)"
              type="text"
              helperText="Optional partial fixity. Supports scientific notation (e.g. 1e6) and spaces as thousand separators."
              value={inputs.column_base_rotational_stiffness_kNm_per_rad}
              onChange={(e) => updateInputs({ column_base_rotational_stiffness_kNm_per_rad: e.target.value })}
              onBlur={(e) =>
                updateInputs({
                  column_base_rotational_stiffness_kNm_per_rad: formatThousandsSpace(
                    String(e.target.value || '').replace(/\s+/g, ''),
                  ),
                })
              }
            />
          </Grid>
          <Grid item xs={12}>
            <Alert severity="info" sx={{ py: 0.5 }}>
              Portal knee modeling: chord-column interfaces are pinned end releases; columns remain continuous.
            </Alert>
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label="Column out-of-plane brace (m)"
              helperText="Leave empty to auto-propose a weak-axis brace interval from the prescreen."
              value={inputs.column_out_of_plane_brace_m}
              onChange={(e) => updateInputs({ column_out_of_plane_brace_m: e.target.value })}
            />
          </Grid>
          {!isBuilding && (
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!inputs.wind_model_manual}
                    onChange={(e) => updateInputs({ wind_model_manual: e.target.checked })}
                  />
                }
                label="Manual wind override (column q + top-chord q)"
              />
            </Grid>
          )}
          {!isBuilding && !!inputs.wind_model_manual && (
            <>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Manual column wind q (kN/m, signed global X on both columns)"
                  type="number"
                  value={inputs.wind_manual_column_kN_m}
                  onChange={(e) => updateInputs({ wind_manual_column_kN_m: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Manual top-chord q (kN/m, + down, − uplift)"
                  type="number"
                  value={inputs.wind_manual_top_chord_kN_m}
                  onChange={(e) => updateInputs({ wind_manual_top_chord_kN_m: e.target.value })}
                />
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <FormControlLabel
              control={<Checkbox checked={!!inputs.second_order_p_delta} onChange={(e) => updateInputs({ second_order_p_delta: e.target.checked })} />}
              label="Second-order P-Δ analysis"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!inputs.portal_include_sway_imperfection}
                  onChange={(e) => updateInputs({ portal_include_sway_imperfection: e.target.checked })}
                />
              }
              label="EN 1993-1-1 §5.3.2 sway imperfection (portal ULS: H = φ V_Ed at eaves)"
            />
            <Typography variant="caption" color="text.secondary" display="block">
              Equivalent horizontal couple ±H/2 at bottom-chord corners; φ = φ₀ αh αm. SLS deflection uses loads without
              this term.
            </Typography>
          </Grid>
        </Grid>
      </Collapse>

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" gutterBottom>
        Loads (characteristic → ULS in backend)
      </Typography>
      <Grid container spacing={2}>
        {isBuilding && inputs.design_code !== 'aisc_lrfd' ? (
          <>
            {showFiSnowMap && (
              <>
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Ground snow s_k source</InputLabel>
                    <Select
                      label="Ground snow s_k source"
                      value={inputs.snow_sk_source || 'manual'}
                      onChange={(e) => updateInputs({ snow_sk_source: e.target.value })}
                    >
                      <MenuItem value="manual">Manual s_k</MenuItem>
                      <MenuItem value="coordinates">From coordinates (YM 6/16 map)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                {inputs.snow_sk_source === 'coordinates' && (
                  <>
                    <Grid item xs={6} sm={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Coordinate system</InputLabel>
                        <Select
                          label="Coordinate system"
                          value={inputs.site_coord_system || 'wgs84'}
                          onChange={(e) => updateInputs({ site_coord_system: e.target.value })}
                        >
                          <MenuItem value="wgs84">WGS84 (lat, lon)</MenuItem>
                          <MenuItem value="tm35fin">ETRS-TM35FIN (N, E)</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    {inputs.site_coord_system === 'tm35fin' ? (
                      <>
                        <Grid item xs={6} sm={4}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Northing (m)"
                            type="number"
                            value={inputs.site_northing}
                            onChange={(e) => updateInputs({ site_northing: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Easting (m)"
                            type="number"
                            value={inputs.site_easting}
                            onChange={(e) => updateInputs({ site_easting: e.target.value })}
                          />
                        </Grid>
                      </>
                    ) : (
                      <>
                        <Grid item xs={6} sm={4}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Latitude (°)"
                            type="number"
                            value={inputs.site_lat}
                            onChange={(e) => updateInputs({ site_lat: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Longitude (°)"
                            type="number"
                            value={inputs.site_lon}
                            onChange={(e) => updateInputs({ site_lon: e.target.value })}
                          />
                        </Grid>
                      </>
                    )}
                    <Grid item xs={12} sm={4}>
                      <Button
                        fullWidth
                        variant="outlined"
                        size="small"
                        disabled={snowLookupBusy}
                        onClick={lookupSnowSk}
                        sx={{ height: '40px' }}
                      >
                        {snowLookupBusy ? 'Looking up…' : 'Look up s_k'}
                      </Button>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        YM-asetus 6/16: väliarvot interpoloidaan suoraviivaisesti lähimpien käyrien välimatkan mukaan
                        (±5 km grid, prescreening).
                      </Typography>
                      {snowLookupError ? (
                        <Alert severity="error" sx={{ mt: 1 }}>
                          {snowLookupError}
                        </Alert>
                      ) : null}
                    </Grid>
                  </>
                )}
              </>
            )}
            {designBasis === 'eurocode_is' && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Iceland snow zone</InputLabel>
                  <Select
                    label="Iceland snow zone"
                    value={inputs.snow_zone_is || ''}
                    onChange={(e) => updateInputs({ snow_zone_is: e.target.value, snow_sk_source: 'is_zone' })}
                  >
                    <MenuItem value="1">Zone 1 — 2.1 kN/m²</MenuItem>
                    <MenuItem value="2">Zone 2 — 2.2 to 3.8 kN/m²</MenuItem>
                    <MenuItem value="3">Zone 3 — 3.9 to 6.1 kN/m²</MenuItem>
                    <MenuItem value="4">Zone 4 — above 6.1 kN/m² (enter s_k)</MenuItem>
                    <MenuItem value="H">Zone H — enter s_k</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={6} sm={4}>
              <TextField
                fullWidth
                size="small"
                label={<EurocodeText>Ground snow s_k (kN/m²)</EurocodeText>}
                type="number"
                value={inputs.sk_kN_m2}
                onChange={(e) => updateInputs({ sk_kN_m2: e.target.value, snow_sk_source: 'manual' })}
                helperText={
                  designBasis === 'eurocode_lv'
                    ? 'Latvia: enter s_k. No LVS snow map is stored.'
                    : 'EN 1991-1-3 · shape factor μ from roof geometry'
                }
                disabled={inputs.snow_sk_source === 'coordinates'}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                fullWidth
                size="small"
                label={<EurocodeText>Exposure C_e</EurocodeText>}
                type="number"
                inputProps={{ step: 0.05, min: 0 }}
                value={inputs.snow_Ce}
                onChange={(e) => updateInputs({ snow_Ce: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                fullWidth
                size="small"
                label={<EurocodeText>Thermal C_t</EurocodeText>}
                type="number"
                inputProps={{ step: 0.05, min: 0 }}
                value={inputs.snow_Ct}
                onChange={(e) => updateInputs({ snow_Ct: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                fullWidth
                size="small"
                label={<EurocodeText>Dead g (kN/m²)</EurocodeText>}
                type="number"
                value={inputs.g_kN_m2}
                onChange={(e) => updateInputs({ g_kN_m2: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Imposed (kN/m²)"
                type="number"
                value={inputs.imposed_kN_m2}
                onChange={(e) => updateInputs({ imposed_kN_m2: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Governing frame: {Number(inputs.g_kN_m2 || 0).toFixed(2)} kN/m² × {Number(inputs.frame_spacing_m || 6).toFixed(2)} m
                = {(Number(inputs.g_kN_m2 || 0) * Number(inputs.frame_spacing_m || 6)).toFixed(2)} kN/m dead; imposed{' '}
                {Number(inputs.imposed_kN_m2 || 0).toFixed(2)} kN/m² × {Number(inputs.frame_spacing_m || 6).toFixed(2)} m
                = {(Number(inputs.imposed_kN_m2 || 0) * Number(inputs.frame_spacing_m || 6)).toFixed(2)} kN/m
              </Typography>
            </Grid>
          </>
        ) : (
          <>
            <Grid item xs={6} sm={4}>
              <TextField
                fullWidth
                size="small"
                label={<EurocodeText>Dead g (kN/m)</EurocodeText>}
                type="number"
                value={inputs.g_kN_m}
                onChange={(e) => updateInputs({ g_kN_m: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Snow (kN/m)"
                type="number"
                value={inputs.snow_kN_m}
                onChange={(e) => updateInputs({ snow_kN_m: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Imposed (kN/m)"
                type="number"
                value={inputs.imposed_kN_m}
                onChange={(e) => updateInputs({ imposed_kN_m: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <Tooltip title="e.g. '5' for kN/m, '5 m/s' for v_b input" arrow>
                <TextField
                  fullWidth
                  size="small"
                  label="Wind"
                  value={inputs.wind_kN_m}
                  onChange={(e) => updateInputs({ wind_kN_m: e.target.value })}
                />
              </Tooltip>
            </Grid>
          </>
        )}
        <Grid item xs={6} sm={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Imposed category</InputLabel>
            <Select
              label="Imposed category"
              value={inputs.imposed_category || 'H'}
              onChange={(e) => updateInputs({ imposed_category: e.target.value })}
            >
              <MenuItem value="H">H — roof (ψ = 0)</MenuItem>
              <MenuItem value="E">E — storage or suspended services</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={4}>
          <TextField
            fullWidth
            size="small"
            label="Bottom chord (kN/m)"
            type="number"
            value={inputs.bottom_chord_kN_m}
            onChange={(e) => updateInputs({ bottom_chord_kN_m: e.target.value })}
          />
        </Grid>
        {isBuilding && (
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!inputs.snow_checkerboard_ul}
                  onChange={(e) => updateInputs({ snow_checkerboard_ul: e.target.checked })}
                />
              }
              label="ULS snow checkerboard (envelope uniform + alternating roof panels)"
            />
          </Grid>
        )}
      </Grid>

      <Box sx={{ mt: 2 }}>
        <FormControlLabel
          control={<Checkbox checked={inputs.self_weight} onChange={(e) => updateInputs({ self_weight: e.target.checked })} />}
          label="Include self-weight"
        />
        <FormControlLabel
          control={<Checkbox checked={inputs.optimize} onChange={(e) => updateInputs({ optimize: e.target.checked })} />}
          disabled={!inputs.manual_sections}
          label="Optimize diagonal (manual mode: lightest compatible)"
        />
      </Box>

      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={6} sm={4}>
          <TextField
            fullWidth
            size="small"
            label="Utilization target"
            type="number"
            inputProps={{ step: 0.05, min: 0.5, max: 1 }}
            value={inputs.utilization_target}
            onChange={(e) => updateInputs({ utilization_target: e.target.value })}
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <TextField
            fullWidth
            size="small"
            label="Defl. limit span/"
            type="number"
            value={inputs.deflection_limit_ratio}
            onChange={(e) => updateInputs({ deflection_limit_ratio: e.target.value })}
          />
        </Grid>
        {inputs.structure_type === 'portal' && !inputs.concentrically_braced_mode && (
          <Grid item xs={6} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="SLS drift limit (h / this ratio)"
              type="number"
              value={inputs.drift_limit_ratio}
              onChange={(e) => updateInputs({ drift_limit_ratio: e.target.value })}
            />
          </Grid>
        )}
        <Grid item xs={12} sm={4}>
          <Tooltip
            title="Auto-design: split diagonals by |N| into this many profile groups (manual: extra picks below)"
            arrow
          >
            <TextField
              fullWidth
              size="small"
              label="Diagonal section groups"
              type="number"
              inputProps={{ step: 1, min: 1, max: 10 }}
              value={inputs.diagonal_group_count}
              onChange={(e) => updateInputs({ diagonal_group_count: e.target.value })}
            />
          </Tooltip>
        </Grid>
        {!inputs.manual_sections && (
          <>
            <Grid item xs={6} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Steel cost (EUR / installed kg)"
                type="number"
                inputProps={{ step: 0.5, min: 0 }}
                helperText="Auto-design objective with member mass estimate"
                value={inputs.cost_eur_per_installed_steel_kg}
                onChange={(e) => updateInputs({ cost_eur_per_installed_steel_kg: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Overlap joint cost (EUR each)"
                type="number"
                inputProps={{ step: 1, min: 0 }}
                helperText="Per bottom-chord panel weld note with overlap on chord face"
                value={inputs.cost_eur_per_overlap_joint}
                onChange={(e) => updateInputs({ cost_eur_per_overlap_joint: e.target.value })}
              />
            </Grid>
          </>
        )}
        <Grid item xs={12}>
          <Tooltip
            title="Default off (typical shop detail per EN 1993-1-8 §7.1.5(8)). Affects overlap under-brace fillet length and joint pre-checks only."
            arrow
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!inputs.overlap_hidden_seam_welded}
                  onChange={(e) => updateInputs({ overlap_hidden_seam_welded: e.target.checked })}
                />
              }
              label="Overlap joint: weld hidden seam on overlapped brace (longer L_eff for under brace)"
            />
          </Tooltip>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Tooltip
            title="Half-sine bow on both chord lines; midspan amplitude = span / ratio (first-order imperfect geometry)."
            arrow
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!inputs.chord_imperfection_include}
                  onChange={(e) => updateInputs({ chord_imperfection_include: e.target.checked })}
                />
              }
              label="Chord imperfection in FEM (top + bottom)"
            />
          </Tooltip>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label="Imperfection span / …"
            type="number"
            inputProps={{ step: 1, min: 1 }}
            value={inputs.chord_imperfection_span_ratio}
            onChange={(e) => updateInputs({ chord_imperfection_span_ratio: e.target.value })}
            disabled={!inputs.chord_imperfection_include}
          />
        </Grid>
        {!isBuilding && (
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Design basis</InputLabel>
              <Select label="Design basis" value={designBasis} onChange={(e) => setDesignBasis(e.target.value)}>
                <MenuItem value="eurocode_fi">Second-generation Eurocode, annex FI (recommended)</MenuItem>
                <MenuItem value="eurocode_is">Second-generation Eurocode, annex IS (recommended)</MenuItem>
                <MenuItem value="eurocode_lv">Eurocode, annex LV (enter s_k and v_b,0)</MenuItem>
                <MenuItem value="aisc">AISC 360 LRFD</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        )}
      </Grid>

      <Button
        fullWidth
        variant="contained"
        color="primary"
        sx={{ mt: 3 }}
        disabled={loading}
        onClick={() => onSubmit?.()}
      >
        Calculate
      </Button>
      {loading && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.25,
            mt: 2,
            py: 0.5,
          }}
          aria-live="polite"
          aria-busy="true"
        >
          <CircularProgress size={22} thickness={4} />
          <Typography variant="body2" color="text.secondary">
            Calculating…
          </Typography>
        </Box>
      )}
      {!loading && lastComputeMs != null && Number.isFinite(lastComputeMs) && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1.5 }}>
          Last calculation: {lastComputeMs < 1000 ? `${Math.round(lastComputeMs)} ms` : `${(lastComputeMs / 1000).toFixed(2)} s`}
        </Typography>
      )}
    </Paper>
  );
}
